//#region INTERFACE IAutoOrderPreparedOrderSummary

/**
 * ### IAutoOrderPreparedOrderSummary
 * A dictionary mapping order IDs to their prepared order summary information.
 *
 * @interface IAutoOrderPreparedOrderSummary
 * @description Represents a collection of prepared order summaries indexed by order ID.
 *
 * @example
 * const summaries: IAutoOrderPreparedOrderSummary = {
 *   "order123": {
 *     supplierId: "SUP456",
 *     summary: {
 *       numItems: 5,
 *       total: 150.00
 *     }
 *   }
 * };
 */
export type IAutoOrderPreparedOrderSummary = Record<string, IAutoOrderPreparedOrderSummaryInfo>;

//#endregion

//#region INTERFACE IAutoOrderPreparedOrderSummaryInfo

/**
 * ### IAutoOrderPreparedOrderSummaryInfo
 * Provides summary information about a prepared order for a specific supplier.
 *
 * @interface IAutoOrderPreparedOrderSummaryInfo
 * @extends Object
 * @description Contains the supplier identifier and a summary object detailing
 *              the number of items and total amount for the prepared order.
 *
 * @example
 * const summaryInfo: IAutoOrderPreparedOrderSummaryInfo = {
 *   supplierId: "SUP456",
 *   summary: {
 *     numItems: 5,
 *     total: 150.00
 *   }
 * };
 */
export interface IAutoOrderPreparedOrderSummaryInfo extends Object {
  /**
   * Summary of the prepared order.
   * @description Includes the total number of items and the total monetary amount.
   *
   * @type {{ numItems: number; total: number }}
   * @example { numItems: 5, total: 150.00 }
   */
  summary: {
    numItems: number;
    total: number;
  };

  /**
   * Unique identifier for the supplier associated with the order.
   * @type {string}
   * @example "SUP456"
   */
  supplierId: string;
}

//#endregion
